/**
 * Admin · Trade lanes (§21, §10 / §B-5). MANAGER sees read-only tables; ADMIN
 * manages trade lanes + IOR legal entities (create/edit/delete) and can run an
 * ambiguous-coverage overlap check. Writes go through the ADMIN API (audited,
 * optimistic concurrency).
 */
import { useCallback, useMemo, useState } from "react";
import { ApiError, api, type AdminTradeLane, type AdminLegalEntity, type LaneOverlap } from "../../lib/api.js";
import { useResource } from "../../lib/useResource.js";
import { useSession } from "../../components/auth.js";
import { humanizeCode } from "../../lib/format.js";
import { Badge, StateChip, DataTable, type Column, Button, Icon, EmptyState, ErrorState, SkeletonRows, useToast } from "../../components/ui/index.js";
import { TradeLaneEditor } from "./tradelanes/TradeLaneEditor.js";
import { LegalEntityEditor } from "./tradelanes/LegalEntityEditor.js";

function countriesSummary(codes: string[]): string {
  if (codes.length === 0) return "—";
  return codes.length <= 4 ? codes.join(", ") : `${codes.slice(0, 4).join(", ")} +${codes.length - 4}`;
}

export function TradeLanes() {
  const { user } = useSession();
  const isAdmin = user?.role === "ADMIN";
  const r = useResource(api.admin.tradeLanes);
  const entitiesRes = useResource(api.admin.legalEntities);
  const storesRes = useResource(api.admin.stores);
  const { toast } = useToast();

  const [laneEdit, setLaneEdit] = useState<AdminTradeLane | null | "new">(null);
  const [entityEdit, setEntityEdit] = useState<AdminLegalEntity | null | "new">(null);
  const [overlaps, setOverlaps] = useState<LaneOverlap[] | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = r.reload;
  const reloadEntities = entitiesRes.reload;
  const lanes = useMemo(() => r.data?.lanes ?? [], [r.data]);
  const entities = useMemo(() => entitiesRes.data?.entities ?? [], [entitiesRes.data]);
  const stores = useMemo(() => (storesRes.data?.stores ?? []).map((s) => ({ id: s.id, label: `${s.name} (${s.region})` })), [storesRes.data]);

  const removeLane = useCallback(
    async (l: AdminTradeLane) => {
      if (!window.confirm(`Delete trade lane “${l.name}”?`)) return;
      setBusy(true);
      try {
        await api.admin.deleteTradeLane(l.id);
        toast("Lane deleted", "success");
        reload();
      } catch {
        toast("Couldn’t delete the lane.", "danger");
      } finally {
        setBusy(false);
      }
    },
    [reload, toast],
  );

  const removeEntity = useCallback(
    async (e: AdminLegalEntity) => {
      if (!window.confirm(`Delete importer “${e.name}”?`)) return;
      setBusy(true);
      try {
        await api.admin.deleteLegalEntity(e.id);
        toast("Entity deleted", "success");
        reloadEntities();
      } catch (err) {
        toast(err instanceof ApiError && err.status === 409 ? "Entity is in use by a lane — reassign first." : "Couldn’t delete the entity.", "danger");
      } finally {
        setBusy(false);
      }
    },
    [reloadEntities, toast],
  );

  const runOverlap = useCallback(async () => {
    setBusy(true);
    try {
      const res = await api.admin.checkLaneOverlap();
      setOverlaps(res.overlaps);
      if (res.overlaps.length === 0) toast("No ambiguous coverage — every country is covered by ≤1 lane per scope.", "success");
    } catch {
      toast("Couldn’t run the overlap check.", "danger");
    } finally {
      setBusy(false);
    }
  }, [toast]);

  const laneColumns = useMemo<Array<Column<AdminTradeLane>>>(() => {
    const base: Array<Column<AdminTradeLane>> = [
      {
        key: "lane",
        header: "Lane",
        cell: (l) => (
          <span className="cell-stack">
            <span className="cell-primary">{l.name}</span>
            <span className="cell-sub cell-mono" title={l.destinationCountries.join(", ")}>{countriesSummary(l.destinationCountries)}</span>
          </span>
        ),
      },
      { key: "incoterm", header: "Incoterm", cell: (l) => <Badge>{l.incoterm}</Badge> },
      { key: "ior", header: "Importer", cell: (l) => humanizeCode(l.iorType) + (l.iorEntityName ? ` · ${l.iorEntityName}` : "") },
      { key: "value", header: "Valuation", cell: (l) => humanizeCode(l.valueBasis) },
      { key: "duties", header: "Duties", cell: (l) => humanizeCode(l.dutiesPayor) },
      { key: "enabled", header: "Status", cell: (l) => <StateChip on={l.enabled} /> },
    ];
    if (!isAdmin) return base;
    return [
      ...base,
      {
        key: "actions",
        header: "",
        cell: (l) => (
          <span className="row-actions">
            <Button variant="ghost" size="sm" icon="edit" iconOnly aria-label={`Edit ${l.name}`} onClick={() => setLaneEdit(l)} />
            <Button variant="ghost" size="sm" icon="x" iconOnly aria-label={`Delete ${l.name}`} disabled={busy} onClick={() => void removeLane(l)} />
          </span>
        ),
      },
    ];
  }, [isAdmin, busy, removeLane]);

  const loading = r.loading && !r.data;

  return (
    <section className="admin">
      <div className="page-head">
        <div className="page-head__title">
          <h1>Trade lanes</h1>
          {r.data && <span className="page-head__count">{lanes.length} lanes · {entities.length} importers</span>}
        </div>
        {isAdmin ? (
          <div className="page-head__actions">
            <Button variant="secondary" size="sm" icon="globe" onClick={() => void runOverlap()} disabled={busy}>Check overlaps</Button>
            <Button variant="primary" size="sm" icon="plus" onClick={() => setLaneEdit("new")}>New lane</Button>
          </div>
        ) : (
          <span className="soon" title="Admin role required to edit">Read-only · admin can edit</span>
        )}
      </div>

      {overlaps && overlaps.length > 0 && (
        <div className="tester">
          <div className="tester__bar">
            <Icon name="alert" size={15} />
            <span className="tester__title">Ambiguous coverage</span>
            <button type="button" className="tester__close" aria-label="Dismiss" onClick={() => setOverlaps(null)}><Icon name="x" size={14} /></button>
          </div>
          <ul className="tester__trace">
            {overlaps.map((o) => (
              <li key={`${o.scope}-${o.countryCode}`} className="trace-row">
                <span className="chip chip--hold">{o.countryCode}</span>
                <span className="trace-row__name">{o.scope}: {o.laneNames.join(" · ")}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {r.error ? (
        <ErrorState title="Couldn’t load this" hint="Retry in a moment." onRetry={r.reload} />
      ) : loading ? (
        <div className="table-card"><SkeletonRows rows={4} cols={6} /></div>
      ) : lanes.length === 0 ? (
        <EmptyState icon="globe" title="No trade lanes" hint={isAdmin ? "Add a lane to drive customs valuation + incoterm." : "An admin can configure lanes."} />
      ) : (
        <div aria-busy={busy}>
          <DataTable ariaLabel="Trade lanes" rows={lanes} getRowKey={(x) => x.id} columns={laneColumns} />
        </div>
      )}

      {/* Importers of record (legal entities) */}
      <div className="page-head" style={{ marginTop: "var(--sp-8)" }}>
        <div className="page-head__title">
          <h2>Importers of record</h2>
        </div>
        {isAdmin && (
          <div className="page-head__actions">
            <Button variant="secondary" size="sm" icon="plus" onClick={() => setEntityEdit("new")}>New importer</Button>
          </div>
        )}
      </div>
      {entities.length === 0 ? (
        <EmptyState icon="globe" title="No importer entities" hint={isAdmin ? "Add IOR entities (EORI/VAT/EIN/CRA) to use ENTITY-type lanes." : undefined} />
      ) : (
        <div aria-busy={busy}>
          <DataTable
            ariaLabel="Importers"
            rows={entities}
            getRowKey={(e) => e.id}
            columns={[
              { key: "name", header: "Entity", cell: (e) => <span className="cell-strong">{e.name}</span> },
              { key: "eori", header: "EORI", cell: (e) => <span className="cell-mono">{e.eori ?? "—"}</span> },
              { key: "vat", header: "VAT", cell: (e) => <span className="cell-mono">{e.vatNumber ?? "—"}</span> },
              ...(isAdmin
                ? [
                    {
                      key: "actions",
                      header: "",
                      cell: (e: AdminLegalEntity) => (
                        <span className="row-actions">
                          <Button variant="ghost" size="sm" icon="edit" iconOnly aria-label={`Edit ${e.name}`} onClick={() => setEntityEdit(e)} />
                          <Button variant="ghost" size="sm" icon="x" iconOnly aria-label={`Delete ${e.name}`} disabled={busy} onClick={() => void removeEntity(e)} />
                        </span>
                      ),
                    } as Column<AdminLegalEntity>,
                  ]
                : []),
            ]}
          />
        </div>
      )}

      {laneEdit !== null && (
        <TradeLaneEditor
          lane={laneEdit === "new" ? null : laneEdit}
          entities={entities}
          stores={stores}
          onClose={() => setLaneEdit(null)}
          onSaved={() => {
            setLaneEdit(null);
            reload();
          }}
        />
      )}
      {entityEdit !== null && (
        <LegalEntityEditor
          entity={entityEdit === "new" ? null : entityEdit}
          onClose={() => setEntityEdit(null)}
          onSaved={() => {
            setEntityEdit(null);
            reloadEntities();
          }}
        />
      )}
    </section>
  );
}
